import { Component } from '@theme/component';
import { formatMoney } from '@theme/shared--money';

const STORAGE_KEY = 'recently_viewed_products';
// v2: names now follow the product-card rule (plp.custom_name → collection
// prefix → first word). Bumped so names cached under the old rule (full
// titles, no custom names) aren't shown again; they're re-backfilled.
const MODEL_NAMES_KEY = 'recently_viewed_product_model_names_v2';
const STORAGE_MAX = 20;

/**
 * Read the recently-viewed product handle list from localStorage.
 * Returns an array of handles (most-recent first). Safe on quota/parse errors.
 * @returns {string[]}
 */
export function readRecentlyViewed() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
	} catch {
		return [];
	}
}

/**
 * Read the handle → model-name map. The model name is the Aeyde "short title"
 * captured server-side on the PDP and stashed here so the recently-viewed
 * carousel can render short labels without having to refetch collection data.
 * @returns {Record<string, string>}
 */
function readModelNames() {
	try {
		const raw = localStorage.getItem(MODEL_NAMES_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch {
		return {};
	}
}

/**
 * Persist the recently-viewed list, capped to STORAGE_MAX.
 * @param {string[]} handles
 */
function writeRecentlyViewed(handles) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, STORAGE_MAX)));
	} catch {
		/* quota / privacy mode — silently drop */
	}
}

/**
 * Persist the handle → model-name map, pruned to the current recent handles.
 * @param {Record<string, string>} names
 * @param {string[]} validHandles
 */
function writeModelNames(names, validHandles) {
	try {
		const pruned = {};
		for (const h of validHandles) {
			if (names[h]) pruned[h] = names[h];
		}
		localStorage.setItem(MODEL_NAMES_KEY, JSON.stringify(pruned));
	} catch {
		/* quota / privacy mode — silently drop */
	}
}

/**
 * Track the current PDP: push the product handle to the front of the recent
 * list, deduping and capping. Also stashes the Aeyde model name so the
 * carousel can render short titles without refetching collection data.
 * Called from an inline script in `sections/product.liquid`.
 * @param {string} handle
 * @param {string} [modelName] - Optional short title (Aeyde collection prefix)
 */
export function trackProductView(handle, modelName) {
	if (!handle) return;
	const current = readRecentlyViewed();
	const next = [handle, ...current.filter((h) => h !== handle)].slice(0, STORAGE_MAX);
	writeRecentlyViewed(next);

	const names = readModelNames();
	if (modelName) names[handle] = modelName;
	writeModelNames(names, next);
}

// Expose the tracker on window so the PDP inline script can call it without
// needing its own module import. `trackProductView` stays as a named export
// for anything that wants to import it directly.
if (typeof window !== 'undefined') {
	window.__trackProductView = trackProductView;
}

/**
 * Remove a handle from the persisted list. Used when a fetch fails (product
 * was deleted from the store or is otherwise unreachable) so we don't retry
 * it on the next page view.
 * @param {string} handle
 */
function removeHandle(handle) {
	const next = readRecentlyViewed().filter((h) => h !== handle);
	writeRecentlyViewed(next);
}

/**
 * Recently Viewed carousel component.
 *
 * Reads handles from localStorage, optionally excludes the current product,
 * fetches each via `/products/{handle}.js` (JSON), clones a Liquid-rendered
 * `<template data-slide-template>` per product, populates the slide fields,
 * and inserts the lot into the inner `<carousel-slider>`'s track. Finally,
 * manually kicks off `carousel._lazyInit()` because the carousel's own
 * IntersectionObserver bailed early on an empty track.
 *
 * If the storage is empty (or filtered to empty), the whole component is
 * removed from the DOM — no empty heading, no 0-slide carousel.
 *
 * Once populated it gets [data-rendered] and dispatches a bubbling
 * `recently-viewed:rendered` event; each slide link carries the product's
 * handle/id/title/type as data attributes (read by analytics).
 *
 * @fires recently-viewed:rendered
 * @extends {Component}
 */
class RecentlyViewedCarousel extends Component {
	/** @type {AbortController | null} */
	#abort = null;

	async connectedCallback() {
		super.connectedCallback();
		this.#abort = new AbortController();
		try {
			await this.#render();
		} catch (err) {
			if (err.name !== 'AbortError') console.error('[recently-viewed] render failed', err);
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abort?.abort();
		this.#abort = null;
	}

	async #backfillModelNames(slides, modelNames) {
		const missing = slides.filter(({ handle }) => !modelNames[handle]);
		if (missing.length === 0) return;

		await Promise.all(
			missing.map(async ({ handle }) => {
				try {
					const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=pdp--model-name`, {
						signal: this.#abort?.signal,
					});
					if (!res.ok) return;
					const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
					const name = doc.body.textContent?.trim();
					if (name) modelNames[handle] = name;
				} catch (err) {
					if (err.name === 'AbortError') throw err;
				}
			})
		);

		writeModelNames(modelNames, readRecentlyViewed());
	}

	async #render() {
		const currentHandle = this.dataset.currentHandle || '';
		const max = parseInt(this.dataset.max, 10) || 8;

		const handles = readRecentlyViewed()
			.filter((h) => h && h !== currentHandle)
			.slice(0, max);

		if (handles.length === 0) {
			this.remove();
			return;
		}

		// Fetch products in parallel — preserve order via indexed array of promises
		const results = await Promise.all(
			handles.map(async (handle) => {
				try {
					const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
						signal: this.#abort?.signal,
					});
					if (!res.ok) throw new Error(`HTTP ${res.status}`);
					return { handle, product: await res.json() };
				} catch (err) {
					if (err.name === 'AbortError') throw err;
					// Drop stale handles from storage so we don't retry them
					removeHandle(handle);
					return { handle, product: null };
				}
			})
		);

		const slides = results.filter((r) => r.product !== null);
		if (slides.length === 0) {
			this.remove();
			return;
		}

		const template = this.querySelector('template[data-slide-template]');
		const track = this.querySelector('.carousel__track');
		if (!template || !track) {
			this.remove();
			return;
		}

		const modelNames = readModelNames();
		await this.#backfillModelNames(slides, modelNames);
		const frag = document.createDocumentFragment();
		for (const { product } of slides) {
			const slide = template.content.cloneNode(true).querySelector('.carousel__slide');
			if (!slide) continue;

			const link = slide.querySelector('[data-product-link]');
			const img = slide.querySelector('[data-product-image]');
			const title = slide.querySelector('[data-product-title]');
			const price = slide.querySelector('[data-product-price]');
			const compare = slide.querySelector('[data-product-compare-price]');

			if (link) {
				link.href = product.url || `/products/${product.handle}`;
				Object.assign(link.dataset, {
					productHandle: product.handle,
					productId: String(product.id),
					productTitle: product.title || '',
					productType: product.type || '',
				});
			}
			if (img && product.featured_image) {
				img.src = product.featured_image;
				img.alt = product.title || '';
			}
			if (title) title.textContent = modelNames[product.handle] || product.title || '';
			const onSale = product.compare_at_price > product.price;
			if (price) {
				price.textContent = formatMoney(product.price);
				price.classList.toggle('recently-viewed__price--sale', onSale);
			}
			if (compare && onSale) {
				compare.textContent = formatMoney(product.compare_at_price);
				compare.hidden = false;
			}

			frag.appendChild(slide);
		}

		track.appendChild(frag);
		this.hidden = false;
		this.dataset.rendered = '';
		this.dispatchEvent(new CustomEvent('recently-viewed:rendered', { bubbles: true }));

		// The inner carousel's IntersectionObserver already fired on an empty
		// track (or may fire later on an empty track) and bails because there
		// are no real slides. Kick it off manually now that the slides exist.
		const carousel = this.querySelector('carousel-slider');
		if (carousel && typeof carousel._lazyInit === 'function') {
			carousel._lazyInit();
		}
	}
}

if (!customElements.get('recently-viewed-carousel')) {
	customElements.define('recently-viewed-carousel', RecentlyViewedCarousel);
}
